import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useFilterKeyboardShortcuts } from '../../../../../hooks/useFilterKeyboardShortcuts';
import { useIsLandscapeTablet } from '../../../../../hooks/useMediaBreakPoints';

// Design system primitives — mismos contenedores que usa `ValidationsTable`
// (FiltersContainer = card lila `backgroundLila`; Title/SubContainerFilters/
// ContainerButtons son los mismos elementos que aparecen en
// HistoricalActionsReport y demás reports del proyecto).
import {
  Title,
  SubContainerFilters,
  ContainerButtons as CanonicalContainerButtons,
} from '../../../../../components/display/tables/components/MainStyledComponents';
import InputCalendar from '../../../../../components/inputs/Calendar';
import MultiSelectDropdown from '../../../../../components/inputs/DropDown/MultiSelectDropdown';
import { Options } from '../../../../../components/inputs/DropDown/MultiSelectDropdown/types';
import { ButtonIconBackground } from '../../../../../components/inputs/buttons/ButtonIconBackground';
import { TimeManagementFilters } from '../types';
import {
  ArrowBottomStyled,
  FiltersWithLoadingStyled,
  ShowFiltersButton,
} from '../styles';

export type { TimeManagementFilters };

interface TeamOption {
  id: string;
  name: string;
}

interface UserOption {
  id: string;
  name: string;
  surName?: string;
}

interface ExternalCompanyOption {
  id: string;
  companyName: string;
}

interface Props {
  initialFilters: TimeManagementFilters;
  defaultFromDate: string;
  defaultToDate: string;
  teamOptions: TeamOption[];
  userOptions: UserOption[];
  externalCompanyOptions: ExternalCompanyOption[];
  isLoading: boolean;
  onSearch: (filters: TimeManagementFilters) => void;
  onClear: (filters: TimeManagementFilters) => void;
  onRefresh: () => void;
}

// Header del card de filtros: contiene el Title y prepara espacio para
// futuros controles (e.g. ShowFiltersButton de mobile collapse). Mismo
// patrón que `ValidationsTable/styles.ts:SubContainerHead`.
const SubContainerHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// ContainerButtons sin override: el margin-left:auto vive en el
// RightCluster wrapper para que arrastre AMBOS (iconos + check) al
// borde derecho como una sola unidad visual.
const ContainerButtons = CanonicalContainerButtons;

// Cluster derecho: agrupa los iconos de acción en un sub-flex pegado al
// borde. Si fueran flex-items independientes del SubContainerFilters, el
// margin-left:auto solo arrastraría uno y el otro wrappearía a la fila
// siguiente (probado).
const RightCluster = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing.l};
  margin-left: auto;
`;

function isoToDateOrUndefined(iso: string): Date | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function dateToISO(d: Date, endOfDay: boolean): string {
  const out = new Date(d);
  if (endOfDay) {
    out.setHours(23, 59, 59, 999);
  } else {
    out.setHours(0, 0, 0, 0);
  }
  return out.toISOString();
}

export function FiltersBar({
  initialFilters,
  defaultFromDate,
  defaultToDate,
  teamOptions,
  userOptions,
  externalCompanyOptions,
  isLoading,
  onSearch,
  onClear,
  onRefresh,
}: Props): JSX.Element {
  const { t } = useTranslation(['forms', 'tables', 'buttons', 'pages', 'common']);

  // Opciones del filtro multiselect de estados de actuación. Se excluyen
  // `annulled` (nunca se representa) y `partial` (transitorio sin horas
  // reales) por diseño del informe. Labels vía `common:states.<x>` (ya
  // existen) — mismas que pinta `SpecialTag`.
  const STATE_OPTIONS: Options = [
    { value: 'open', label: t('states.open', { ns: 'common' }) },
    { value: 'validated', label: t('states.validated', { ns: 'common' }) },
    {
      value: 'pending-certification',
      label: t('states.pending-certification', { ns: 'common' }),
    },
    { value: 'end', label: t('states.end', { ns: 'common' }) },
  ];

  // Collapse mobile: en landscape tablet (y portrait tablet) los filtros
  // empiezan ocultos y se expanden con el ShowFiltersButton. En desktop
  // siempre visibles. Mismo patrón que ValidationsTable (líneas 220, 233,
  // 342-344, 1485-1490).
  const isLanscapeTablet = useIsLandscapeTablet();
  const [showFilters, setShowFilters] = useState(true);
  useEffect(() => {
    setShowFilters(!isLanscapeTablet);
  }, [isLanscapeTablet]);
  const handleToggleFilters = () => setShowFilters((s) => !s);

  const [fromDate, setFromDate] = useState(initialFilters.fromDate);
  const [toDate, setToDate] = useState(initialFilters.toDate);
  const [teamIds, setTeamIds] = useState<string[]>(initialFilters.teamIds);
  const [userIds, setUserIds] = useState<string[]>(initialFilters.userIds);
  const [externalCompanyIds, setExternalCompanyIds] = useState<string[]>(
    initialFilters.externalCompanyIds,
  );
  const [states, setStates] = useState<string[]>(initialFilters.states);

  // `InputCalendar` toma `dateRange` solo como valor INICIAL (no re-sincroniza
  // al cambiar la prop). Para que al "Borrar" los inputs de fecha se reseteen
  // VISUALMENTE a las fechas por defecto (no solo en la query), forzamos su
  // remontaje cambiando este `key` únicamente en handleClear. No se usa en
  // cada cambio de fecha (eso desmontaría el picker a mitad de selección).
  const [calendarResetNonce, setCalendarResetNonce] = useState(0);

  const teamsAsOptions: Options = teamOptions.map((tm) => ({
    value: tm.id,
    label: tm.name,
  }));

  const usersAsOptions: Options = userOptions.map((u) => ({
    value: u.id,
    label: `${u.name}${u.surName ? ` ${u.surName}` : ''}`.trim(),
  }));

  const externalCompaniesAsOptions: Options = externalCompanyOptions.map(
    (c) => ({
      value: c.id,
      label: c.companyName,
    }),
  );

  const handleRangeChange = (range: { from?: Date; to?: Date }) => {
    if (range.from) setFromDate(dateToISO(range.from, false));
    if (range.to) setToDate(dateToISO(range.to, true));
  };

  const handleSearch = () => {
    onSearch({
      fromDate,
      toDate,
      teamIds,
      userIds,
      externalCompanyIds,
      states,
    });
  };

  const handleClear = () => {
    setFromDate(defaultFromDate);
    setToDate(defaultToDate);
    setTeamIds([]);
    setUserIds([]);
    setExternalCompanyIds([]);
    // Sin default de estado: "Borrar" deja el filtro VACÍO = todos los
    // no-anulados (igual que el resto de filtros), no re-aplica Validada+Pendiente.
    setStates([]);
    // Remonta el InputCalendar para que muestre las fechas por defecto.
    setCalendarResetNonce((n) => n + 1);
    onClear({
      fromDate: defaultFromDate,
      toDate: defaultToDate,
      teamIds: [],
      userIds: [],
      externalCompanyIds: [],
      states: [],
    });
  };

  const initialFrom = isoToDateOrUndefined(fromDate);
  const initialTo = isoToDateOrUndefined(toDate);

  // Atajos de teclado canónicos (`useFilterKeyboardShortcuts`):
  //   Enter dentro del card → handleSearch
  //   Shift+Enter dentro del card → handleClear
  // Mismo hook que usan ValidationsTable, AccidentsTable, InputRadioTagTable.
  // El ref se ancla a `SubContainerFilters` (el div padre de todos los
  // inputs); el hook filtra eventos por `event.target.closest(ref)`.
  const filterContainerRef = useRef<HTMLDivElement>(null);
  useFilterKeyboardShortcuts(
    filterContainerRef,
    handleSearch,
    handleClear,
    isLoading,
  );

  return (
    <FiltersWithLoadingStyled
      $isLoading={isLoading}
      data-testid="time-management-filters"
    >
      <SubContainerHead>
        <Title component="h2" variant="semiBold" size="M">
          {t('pages:timeManagement.filterBy')}
        </Title>
        {isLanscapeTablet && (
          <ShowFiltersButton
            $isBlack={showFilters}
            onClick={handleToggleFilters}
            data-testid="filter-toggle-btn"
            aria-label={t('tables:filters.toggle', {
              defaultValue: 'Mostrar/Ocultar filtros',
            })}
          >
            <ArrowBottomStyled $isRotate={showFilters} />
          </ShowFiltersButton>
        )}
      </SubContainerHead>

      {/*
        Canonical layout (ver ValidationsTable):
        - <SubContainerFilters> es un único flex-wrap.
        - Los filtros van como hijos directos.
        - <ContainerButtons> es el ÚLTIMO hijo, no un hermano: gracias al
          wrap aterriza pegado a la derecha del último input.
      */}
      {showFilters && (
      <SubContainerFilters ref={filterContainerRef}>
        <InputCalendar
          key={calendarResetNonce}
          label={t('forms:timeManagement.filters.dateRange')}
          name="time-management-date-range"
          dateRange={
            initialFrom && initialTo
              ? { from: initialFrom, to: initialTo }
              : undefined
          }
          onChangeInput={handleRangeChange}
        />

        <MultiSelectDropdown
          inputSize="L"
          typeDropDown="Default"
          searchable
          placeholder={t('forms:timeManagement.filters.teams')}
          labelText={t('forms:timeManagement.filters.teams')}
          options={teamsAsOptions}
          selectedValues={teamIds}
          onChangeSelected={setTeamIds}
        />

        <MultiSelectDropdown
          inputSize="L"
          typeDropDown="Default"
          searchable
          placeholder={t('forms:timeManagement.filters.users')}
          labelText={t('forms:timeManagement.filters.users')}
          options={usersAsOptions}
          selectedValues={userIds}
          onChangeSelected={setUserIds}
        />

        <MultiSelectDropdown
          inputSize="L"
          typeDropDown="Default"
          searchable
          placeholder={t('forms:timeManagement.filters.externalCompanies')}
          labelText={t('forms:timeManagement.filters.externalCompanies')}
          options={externalCompaniesAsOptions}
          selectedValues={externalCompanyIds}
          onChangeSelected={setExternalCompanyIds}
        />

        {/*
          Filtro multiselect de estados (reemplaza el antiguo check "Solo
          validados"). Espejo exacto de los selectores de equipos/operarios/
          empresas. Label/placeholder = `tables:headers.status` ("Estat").
        */}
        <MultiSelectDropdown
          inputSize="L"
          typeDropDown="Default"
          searchable
          placeholder={t('tables:headers.status')}
          labelText={t('tables:headers.status')}
          options={STATE_OPTIONS}
          selectedValues={states}
          onChangeSelected={setStates}
        />

        {/*
          Cluster derecho: iconos de acción (borrar / refrescar / buscar)
          pegados al borde derecho aunque cambien los filtros.
        */}
        <RightCluster>
          <ContainerButtons>
            <ButtonIconBackground
              variant="delete"
              onClick={handleClear}
              disabled={isLoading}
              title={`${t('tables:filters.clearFilters')} (Shift+Enter)`}
              data-testid="filter-clear-btn"
            />
            {/* Sin title: patrón canónico de ValidationsTable.tsx:1677 — el
                icono restart se basta solo. Antes ponía title="Restablecer"
                que sonaba a reset (limpiar todo), no a refrescar. */}
            <ButtonIconBackground
              variant="restart"
              onClick={onRefresh}
              disabled={isLoading}
              data-testid="filter-refresh-btn"
            />
            <ButtonIconBackground
              variant="search"
              onClick={handleSearch}
              disabled={isLoading}
              title={`${t('buttons:search')} (Enter)`}
              data-testid="filter-search-btn"
            />
          </ContainerButtons>
        </RightCluster>
      </SubContainerFilters>
      )}
    </FiltersWithLoadingStyled>
  );
}
